import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformPaiement } from "@/store/platform/bloc6";
import { toMonthKey } from "@/pages/proprietaire/loyers/periods";
import type { Paiement, PaiementInput } from "./types";

interface PaiementsContextValue {
  paiements: Paiement[];
  addPaiement: (input: PaiementInput) => Paiement;
  getPaiement: (id: string) => Paiement | undefined;
  removePaiement: (id: string) => void;
  paiementsByContrat: (contratId: string) => Paiement[];
  paiementsByContratPeriode: (contratId: string, periode: string) => Paiement[];
  totalPaye: (contratId: string, periode: string) => number;
  totalPayeContrat: (contratId: string) => number;
  stats: { total: number; montantTotal: number; ceMois: number };
}

const PaiementsContext = createContext<PaiementsContextValue | null>(null);

function toPaiement(source: PlatformPaiement): Paiement {
  return {
    id: source.id,
    contratId: source.contratId,
    periode: source.periode,
    montant: source.montant,
    date: source.date,
    mode: source.mode,
    reference: source.reference,
    note: source.note,
    createdAt: source.createdAt,
  };
}

/**
 * Vue « Propriétaire » des paiements du store unifié de la plateforme.
 * Les paiements proviennent RÉELLEMENT de la table `paiements` (via
 * `v_paiements_details`). Chaque opération passe par Supabase : le trigger
 * recalculant le statut du loyer s'applique automatiquement.
 */
export function PaiementsProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const paiements = useMemo(
    () => platform.paiements.filter((item) => item.scope === "proprietaire").map(toPaiement),
    [platform.paiements]
  );

  const addPaiement = useCallback(
    (input: PaiementInput) => toPaiement(platform.addPaiement(input)),
    [platform.addPaiement]
  );

  const removePaiement = useCallback(
    (id: string) => {
      platform.removePaiement(id);
    },
    [platform.removePaiement]
  );

  const getPaiement = useCallback((id: string) => paiements.find((item) => item.id === id), [paiements]);

  const paiementsByContrat = useCallback(
    (contratId: string) =>
      paiements
        .filter((item) => item.contratId === contratId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [paiements]
  );

  const paiementsByContratPeriode = useCallback(
    (contratId: string, periode: string) =>
      paiements.filter((item) => item.contratId === contratId && item.periode === periode),
    [paiements]
  );

  const totalPaye = useCallback(
    (contratId: string, periode: string) =>
      paiements
        .filter((item) => item.contratId === contratId && item.periode === periode)
        .reduce((sum, item) => sum + item.montant, 0),
    [paiements]
  );

  const totalPayeContrat = useCallback(
    (contratId: string) =>
      paiements.filter((item) => item.contratId === contratId).reduce((sum, item) => sum + item.montant, 0),
    [paiements]
  );

  const stats = useMemo(() => {
    const moisCourant = toMonthKey(new Date());
    return {
      total: paiements.length,
      montantTotal: paiements.reduce((sum, item) => sum + item.montant, 0),
      ceMois: paiements
        .filter((item) => toMonthKey(new Date(item.date)) === moisCourant)
        .reduce((sum, item) => sum + item.montant, 0),
    };
  }, [paiements]);

  const value = useMemo(
    () => ({
      paiements,
      addPaiement,
      getPaiement,
      removePaiement,
      paiementsByContrat,
      paiementsByContratPeriode,
      totalPaye,
      totalPayeContrat,
      stats,
    }),
    [
      paiements,
      addPaiement,
      getPaiement,
      removePaiement,
      paiementsByContrat,
      paiementsByContratPeriode,
      totalPaye,
      totalPayeContrat,
      stats,
    ]
  );

  return <PaiementsContext.Provider value={value}>{children}</PaiementsContext.Provider>;
}

export function usePaiements(): PaiementsContextValue {
  const context = useContext(PaiementsContext);
  if (!context) {
    throw new Error("usePaiements doit être utilisé à l'intérieur de PaiementsProvider");
  }
  return context;
}